import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { UseVoiceSessionReturn } from '@orborix/voice-client';

/**
 * Reason del `session.blocked` que llega a speak: saldo agotado. El otro reason
 * del contrato (`coach_safety_l4`) es del coach y nunca viaja por este módulo.
 * Se comparte con la card terminal para no repetir el literal.
 */
export const OUT_OF_CREDITS_REASON = 'insufficient_credits';

/**
 * Cierre server-side de la sesión de voz: escucha el window event
 * `orborix:session.ended` (redisparado por `@orborix/voice-client` cuando el
 * backend finaliza la fila por iniciativa propia — hoy, el watch
 * idle-no-speech: 60s sin habla → nudge, +120s → cierre limpio).
 *
 * Devuelve el `reason` del PRIMER evento (o `null` si la sesión sigue viva).
 * El cierre es terminal para esta instancia de la pantalla: eventos
 * posteriores no lo pisan, y el estado sólo se resetea remontando la
 * conversación (el flujo "Retomar la charla" de `ConversationScreen`).
 */
export function useServerSessionEnded(): string | null {
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    const handler = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ reason?: unknown }>).detail;
      const next =
        typeof detail?.reason === 'string' && detail.reason.length > 0
          ? detail.reason
          : 'unknown';
      setReason((prev) => prev ?? next);
    };
    window.addEventListener('orborix:session.ended', handler);
    return () => window.removeEventListener('orborix:session.ended', handler);
  }, []);

  return reason;
}

/**
 * Corte server-side por SALDO: el backend emite `session.blocked`
 * (`insufficient_credits` — preflight al conectar o fusible de overdraft
 * post-turno) y cierra el WS con 4402.
 *
 * El voice-client ya lo marca como cierre deliberado (igual que
 * `session.ended`) — la reconexión no corre. El `stop()` de acá queda como
 * teardown explícito de la pantalla: suelta mic/player sin esperar el close y
 * resetea el `sessionId` del hook (de ahí el `lastSessionIdRef` del
 * controller). Es idempotente con el cleanup del package.
 *
 * Terminal para esta instancia de la pantalla, igual que el cierre por idle.
 */
export function useSessionBlocked(args: {
  sessionRef: RefObject<UseVoiceSessionReturn | null>;
}): boolean {
  const { sessionRef } = args;
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const handler = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ reason?: unknown }>).detail;
      if (detail?.reason !== OUT_OF_CREDITS_REASON) return;
      void sessionRef.current?.stop();
      setBlocked(true);
    };
    window.addEventListener('orborix:session.blocked', handler);
    return () => window.removeEventListener('orborix:session.blocked', handler);
  }, [sessionRef]);

  return blocked;
}

export interface SessionClosingState {
  /** Segundos restantes al momento del preaviso (la card cuenta desde acá). */
  inSeconds: number;
}

/**
 * Preaviso del cierre por inactividad (`orborix:session.closing`, emitido por
 * el watch idle-no-speech del backend `IDLE_CLOSE_WARN_MS` antes del cierre):
 * la UI muestra un countdown accionable. Se retira con
 * `orborix:session.closing.cancelled` (el user dio señal de vida) o con el
 * `session.ended` (la card terminal lo reemplaza).
 */
export function useSessionClosing(): SessionClosingState | null {
  const [closing, setClosing] = useState<SessionClosingState | null>(null);

  useEffect(() => {
    const onClosing = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ inSeconds?: unknown }>).detail;
      if (typeof detail?.inSeconds !== 'number' || detail.inSeconds <= 0) return;
      setClosing({ inSeconds: detail.inSeconds });
    };
    const clear = (): void => setClosing(null);
    window.addEventListener('orborix:session.closing', onClosing);
    window.addEventListener('orborix:session.closing.cancelled', clear);
    window.addEventListener('orborix:session.ended', clear);
    return () => {
      window.removeEventListener('orborix:session.closing', onClosing);
      window.removeEventListener('orborix:session.closing.cancelled', clear);
      window.removeEventListener('orborix:session.ended', clear);
    };
  }, []);

  return closing;
}
